import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppBar, Box, Drawer, Fade, useTheme } from "@mui/material";
import { Menu as MenuIcon, Dashboard, Store, LocalShipping, Build, AutoAwesome, Article } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { headerCategoriesService } from "../services/headerCategories";
import { subcategoriesService, Subcategory } from "../services/subcategories";
import LiveChat from "../components/LiveChat";
import Footer from "../components/Footer";
import { ScrollToTop } from "../components";
import DrawerContent from "./components/DrawerContent";
import { HeaderCategory, NavItem } from "./components/main-layout.types";
import HeaderTopBar from "./components/HeaderTopBar";
import NavigationBar from "./components/NavigationBar";
import MobileSearchBar from "./components/MobileSearchBar";
import CategoriesBar from "./components/CategoriesBar";
import SubcategoriesBar from "./components/SubcategoriesBar";

const MainLayout: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user, signOut } = useAuth();
  const { getItemCount } = useCart();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isRTL = i18n.language === "ar";
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [condition, setCondition] = useState("");
  const [priceRange, setPriceRange] = useState("");
  const [headerCategories, setHeaderCategories] = useState<HeaderCategory[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);

  const navItems: NavItem[] = [
    { path: "/category/all", label: t("nav.products"), icon: <Store />, public: true },
    { path: "/reviews", label: t("nav.reviews"), icon: <AutoAwesome />, public: true },
    { path: "/mediblog", label: t("nav.mediblog"), icon: <Article />, public: true },
    { path: "/logistics", label: t("nav.logistics"), icon: <LocalShipping />, public: false },
    { path: "/maintenance", label: t("nav.maintenance"), icon: <Build />, public: false },
  ];

  const defaultCategories: HeaderCategory[] = [
    { key: "imaging", label: t("productCategories.imaging") },
    { key: "surgical", label: t("productCategories.surgical") },
    { key: "monitoring", label: t("productCategories.monitoring") },
    { key: "laboratory", label: t("productCategories.laboratory") },
    { key: "dental", label: t("productCategories.dental") },
  ];

  const categories = headerCategories.length > 0 ? headerCategories : defaultCategories;



  useEffect(() => {
    const loadCategories = async () => {
      const { data, error } = await headerCategoriesService.getActiveCategories();
      if (error || !data) return;

      const lang = i18n.language;
      const mapped = data.map((cat) => {
        const customLabel =
          lang === "ar" ? cat.label_ar : lang === "fr" ? cat.label_fr : cat.label_en;
        const fallback = t(`productCategories.${cat.key}` as any);
        return {
          key: cat.key,
          label: customLabel && customLabel.trim().length > 0 ? customLabel : fallback,
        };
      });

      setHeaderCategories(mapped);

      // Load subcategories for nested navigation
      const { data: subs } = await subcategoriesService.getActiveSubcategories();
      if (subs) setSubcategories(subs);
    };

    const loadSubcategories = async () => {
      const { data } = await subcategoriesService.getActiveSubcategories();
      if (data) setSubcategories(data);
    };

    loadCategories();
    loadSubcategories();
  }, [i18n.language, t]);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorElUser(event.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);
  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);
  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value);
  const handleFilterChange = (
    key: "category" | "condition" | "priceRange",
    value: string,
  ) => {
    if (key === "category") setCategory(value);
    if (key === "condition") setCondition(value);
    if (key === "priceRange") setPriceRange(value);
  };
  const handleCategoryNavigate = (value: string) => {
    if (!value) {
      if (location.pathname !== "/") {
        navigate("/");
      }
      return;
    }

    setCategory(value);

    if (value.startsWith("/")) {
      navigate(value);
      return;
    }

    navigate(`/category/${value}`);
  };

  return (
    <>
      <ScrollToTop />
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <HeaderTopBar
          anchorElUser={anchorElUser}
          onCloseUserMenu={handleCloseUserMenu}
          onOpenUserMenu={handleOpenUserMenu}
          onSignOut={handleSignOut}
          user={user}
          t={t}
          theme={theme}
          isRTL={isRTL}
          search={search}
          category={category}
          condition={condition}
          priceRange={priceRange}
          categories={categories}
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
          onCategoryNavigate={handleCategoryNavigate}
          onDrawerToggle={handleDrawerToggle}
          getItemCount={getItemCount}
        />
        <AppBar position="static" elevation={0} color="transparent">
          <NavigationBar
            t={t}
            theme={theme}
            isRTL={isRTL}
            user={user}
            navItems={navItems}
          />
        </AppBar>
        <CategoriesBar
          t={t}
          theme={theme}
          category={category}
          categories={categories}
          onCategoryChange={handleCategoryNavigate}
          subcategories={subcategories}
        />
        <SubcategoriesBar
          theme={theme}
          activeCategory={category}
          subcategories={subcategories}
          language={i18n.language}
          onSubcategoryClick={handleCategoryNavigate}
        />
        <MobileSearchBar
          t={t}
          theme={theme}
          isRTL={isRTL}
          search={search}
          category={category}
          condition={condition}
          priceRange={priceRange}
          categories={categories}
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
          onCategoryNavigate={handleCategoryNavigate}
        />
        <Drawer anchor={isRTL ? "right" : "left"} open={mobileOpen} onClose={handleDrawerToggle}>
          <DrawerContent
            user={user}
            handleDrawerToggle={handleDrawerToggle}
            handleSignOut={handleSignOut}
            getItemCount={getItemCount}
            navItems={navItems}
          />
        </Drawer>
        <Box component="main" sx={{ flexGrow: 1 }}>
          <Outlet />
        </Box>
        <Footer />
        <Fade in={Boolean(user)}>
          <Box>
            <LiveChat />
          </Box>
        </Fade>
      </Box>
    </>
  );
};

export default MainLayout;
